import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './Dashboard.css';
import { useNavigate } from "react-router-dom";


interface Seller {
  profile_picture: any;
  id: number;
  username: string;
  email: string;
  access_level: string;
  products: number;
  average_rating: number;
}

interface Feedback {
  id: number;
  rating: number;
  comment: string;
  sender_username: string;
  recipient_username: string;
  product_id: number;
}

interface Product {
  id: number;
  name: string;
  price: number;
  category: string;
  seller_username: string;
  created_at: string;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'sellers' | 'feedback' | 'products'>('sellers');
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState<'all' | 'seller' | 'buyer'>('all');
  // pop-ups
const [showDeleteModal, setShowDeleteModal]   = useState(false);
const [showSuccessModal, setShowSuccessModal] = useState(false);

// track which product is being deleted
const [productToDelete, setProductToDelete]   = useState<Product | null>(null);
const navigate = useNavigate();

const handleDeleteProduct = async () => {
  if (!productToDelete) return;
  try {
    await api.delete(`/products/${productToDelete.id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    // remove from local list
    setProducts((prev) => prev.filter((p) => p.id !== productToDelete.id));
    setShowDeleteModal(false);
    setShowSuccessModal(true);           // ✓ pop-up
  } catch (err) {
    console.error("Cannot delete an item that has Transactions", err);
    alert("Cannot delete an item that has Transactions.");  // swap to custom error if you like
  }
};

const resolveProfilePic = (path?: string): string =>
  path && /^https?:\/\//i.test(path)
    ? path
    : path
    ? `https://new-thriftshop.onrender.com/static/uploads/${path}`
    : "/placeholder.svg";

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const [usersRes, feedbackRes, productRes] = await Promise.all([
          api.get("/admin/users/"),
          api.get("/admin/feedbacks/"),
          api.get("/products/")
        ]);

        setSellers(usersRes.data || []);
        setFeedbacks(feedbackRes.data || []);
        setProducts(productRes.data.products || []);
      } catch (err) {
        console.error("Failed to load dashboard data", err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.access_level === "admin") {
      loadDashboard();
    }
  }, [user]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container">
          <div className="loading">Loading...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="dashboard-container">
        <div className="container">
          <div className="dashboard-content">
            <h1 className="page-title">Admin Dashboard</h1>

            <div className="dashboard-summary">
              <div className="summary-card">
                <h3>Total Users</h3>
                <p className="summary-value">{sellers.length}</p>
              </div>
              <div className="summary-card">
                <h3>Total Products</h3>
                <p className="summary-value">{products.length}</p>
              </div>
            </div>

            <div className="dashboard-tabs">
              <button
                className={`tab-btn ${activeTab === 'sellers' ? 'active' : ''}`}
                onClick={() => setActiveTab('sellers')}
              >
                Users
              </button>
              <button
                className={`tab-btn ${activeTab === 'feedback' ? 'active' : ''}`}
                onClick={() => setActiveTab('feedback')}
              >
                Feedback
              </button>
              <button
                className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
                onClick={() => setActiveTab('products')}
              >
                Products
              </button>
            </div>

            <div className="dashboard-tab-content">
            {activeTab === 'sellers' && (
  <div className="sellers-list">
    <div className="filter-row">
      <label htmlFor="role-filter">Filter by Role:</label>
      <select
        id="role-filter"
        value={filterRole}
        onChange={(e) => setFilterRole(e.target.value as 'all' | 'seller' | 'buyer')}
      >
        <option value="all">All Users</option>
        <option value="seller">Sellers</option>
        <option value="buyer">Buyers</option>
      </select>
    </div>

    <div className="sellers-grid">
      {sellers
        .filter(seller => filterRole === 'all' || seller.access_level === filterRole)
        .map(seller => (
          <div className="seller-card" key={seller.id}>
            <div className="seller-card-header">
  {seller.profile_picture ? (
    <img
    src={resolveProfilePic(seller.profile_picture)}
    alt="Profile"
    className="card-profile-img"
  />
  
  ) : (
    <div className="card-placeholder-img">👤</div>
  )}
  <h3>{seller.username}</h3>
</div>
<p>Email: {seller.email}</p>

            <p>Access Level: {seller.access_level}</p>
            <p>Total Products: {seller.products}</p>
            <p>Rating: {seller.average_rating !== null ? seller.average_rating.toFixed(1) : "N/A"}</p>
            <button className="btn-view" onClick={() => navigate(`/admin/users/${seller.id}`)}>
  View
</button>
          </div>
        ))}
    </div>
  </div>
)}



              {activeTab === 'feedback' && (
                <div className="feedback-list">
                  {feedbacks.map(fb => (
                    <div key={fb.id} className="feedback-card">
                      <div className="feedback-header">
                        <h3>Product ID: {fb.product_id}</h3>
                        <div className="feedback-rating">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <span key={i} className={`star ${i < fb.rating ? 'filled' : ''}`}>★</span>
                          ))}
                        </div>
                      </div>
                      <p className="feedback-comment">{fb.comment}</p>
                      <p className="feedback-seller">
                        From: {fb.sender_username} → To: {fb.recipient_username}
                      </p>
                    </div>
                  ))}
                </div>
              )}

{activeTab === 'products' && (
  <div className="products-grid">
    {products.map(product => (
      <div className="product-card" key={product.id}>
        <h3>{product.name}</h3>
        <p>Price: RM{product.price}</p>
        <p>Category: {product.category}</p>
        <p>Seller: {product.seller_username}</p>
        <p>Created: {new Date(product.created_at).toLocaleDateString()}</p>
        <button
  className="btn-delete"
  onClick={() => {
    setProductToDelete(product);   // remember which product
    setShowDeleteModal(true);      // open confirm modal
  }}
>
  Delete
</button>
      </div>
    ))}
  </div>
)}

            </div>
          </div>
        </div>
      </div>
      {/* —— Confirm-Delete Modal —— */}
{showDeleteModal && productToDelete && (
  <div className="modal-overlay">
    <div className="confirm-modal">
      <div className="warning-icon">!</div>
      <h3>
        Delete “<em>{productToDelete.name}</em>”?
      </h3>
      <div className="confirm-actions">
        <button onClick={handleDeleteProduct} className="confirm-delete-btn">
          Delete
        </button>
        <button onClick={() => setShowDeleteModal(false)} className="cancel-delete-btn">
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

{/* —— Success Modal —— */}
{showSuccessModal && (
  <div className="modal-overlay">
    <div className="success-modal">
      <div className="success-icon">✓</div>
      <h3>Product deleted successfully!</h3>
      <button onClick={() => setShowSuccessModal(false)} className="return-btn">
        Return
      </button>
    </div>
  </div>
)}

    </>
  );
};

export default Dashboard;
